import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Pricing, type PricingPlan } from "@/components/ui/pricing";
import { PLANS as PLAN_DEFS } from "@/lib/plans";
import { trackCtaClick } from "@/lib/ctaTracking";
import { CtaLink } from "@/components/landing/CtaLink";

const YEARLY_TOTALS: Record<string, string> = {
  professional: "3,990",
  team: "9,990",
};

const PLANS: (PricingPlan & { id: string })[] = PLAN_DEFS.map(p => ({
  id: p.id,
  name: p.name.toUpperCase(),
  price: p.price !== null ? String(p.price) : "Custom",
  yearlyPrice: p.yearlyPrice !== null ? String(p.yearlyPrice) : "Custom",
  yearlyTotal: YEARLY_TOTALS[p.id],
  period: p.price !== null ? "month" : "",
  features: p.features,
  description: p.description,
  buttonText: p.price === null ? "Book a demo" : "Get started",
  href: p.price === null
    ? `/book-demo`
    : `/signup?plan=${p.id}`,
  isPopular: !!p.popular,
}));

export function PricingSection() {
  const navigate = useNavigate();

  const handlePlanSelect = (plan: PricingPlan) => {
    const matched = PLANS.find(p => p.name === plan.name);
    if (!matched) return;
    if (matched.id === 'enterprise') {
      void trackCtaClick('book_demo', 'pricing_enterprise');
      navigate('/book-demo');
      return;
    }
    void trackCtaClick('get_started', `pricing_${matched.id}`);
    navigate(`/signup?plan=${matched.id}`);
  };

  return (
    <section id="pricing" className="py-[80px] md:py-[100px] lg:py-[120px] relative scroll-mt-16">
      <div className="max-w-[1200px] mx-auto px-6">
        <Pricing
          plans={PLANS}
          title="Close more deals without enterprise pricing"
          description="Most teams recover their cost in a single won deal."
          onPlanSelect={handlePlanSelect}
          sectionLabel="Invest in Winning"
          billingNote="No long-term contracts. Cancel anytime."
        />

        {/* Footer note — sits between the plan pillars and the comparison box */}
        <motion.p
          initial={false}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25 }}
          className="text-center text-sm font-normal uppercase text-muted-foreground mt-8"
        >
          Billed monthly · Annual plans available · Cancel anytime · Prices in USD
        </motion.p>

        {/* Pricing Comparison */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col items-center gap-3 px-5 py-5 md:px-8 md:py-6 rounded-xl border border-primary/10 landing-card max-w-full">
            <p className="text-base md:text-xl font-medium text-foreground/80 text-center">
              Traditional proposal tools start at <span className="text-muted-foreground line-through">$30,000/year</span>
            </p>
            <p className="text-base md:text-xl font-medium text-orange-300 text-center">
              Win your next deal for less than $399/month
            </p>
            <p className="text-sm text-white/70 mt-1">
              Enterprise-grade intelligence at a fraction of the cost
            </p>
          </div>
        </motion.div>

        {/* Not sure? Book a demo */}
        <motion.p
          initial={false}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Not sure which plan is right?{" "}
          <CtaLink
            to="/book-demo"
            cta="book_demo"
            location="pricing_footer"
            className="text-primary hover:text-primary/80 transition-colors font-medium"
          >
            Book a demo →
          </CtaLink>
        </motion.p>
      </div>
    </section>
  );
}

